import { Injectable } from '@angular/core';
import { tap, map } from 'rxjs/operators';
import { retry, catchError } from 'rxjs/operators';

import { AuthStore, ReturnAuthState } from './auth.store';
import { AuthHttpService } from './auth-http-service';
import { AuthQuery } from '@app/auth/auth.query';
import { resetStores } from '@datorama/akita';
import { PermissionService } from '../services/permission.service';

@Injectable({
  providedIn: 'root',
})
export class AuthDataService {
  arMonate = [];

  constructor(
    private authStore: AuthStore, 
    private authHttpService: AuthHttpService, 
    private authQuery: AuthQuery,
    private permissionService: PermissionService
  ) {}

  login(creds: any) {
    return this.authHttpService.login(creds).pipe(
      tap((data: ReturnAuthState) => {
        // we have to do some mapping
        // as the return is more than
        // we want in the auth store
        let auth = {
          appid: data.appid,
          access_token: data.access_token,
          token_type: data.token_type,
          expires_in: data.expires_in,
          refresh_token: data.refresh_token,

          name: data.name,
          company: data.company,
          email: data.email,
          role: data.role,
          profile_image: data.profile_image,

          firststart: data.firststart,
          firststart_done: data.firststart_done,
          version: data.version,
          appdata: data.appdata,
          userid: data.userid,
          fid: data.fid,

          isactivated: data.isactivated,
          isapproved: data.isapproved,
          status: data.status,

          country: data.country,

          phone_id: data.phone_id,
          gemeinde_key: data.gemeinde_key,
          accesspoint: data.accesspoint,
          request_ip: data.request_ip,

          dienstnr: data.dienstnr,
          dienstname: data.dienstname,
          dienstnr_stvo: data.dienstnr_stvo,
          sachbereich: data.sachbereich,
        };

        this.authStore.save(auth);
        
        // Set permissions in permission service
        this.permissionService.setUserPermissions(data);
      })
    );
  }

  register(data: any) {
    return this.authHttpService.register(data).pipe(
      tap((data) => {
        // we have to do some mapping
        // as the return is more than
        // we want in the auth store
        let auth = {
          appid: data.appid,
          access_token: data.access_token,
          token_type: data.token_type,
          expires_in: data.expires_in,
          refresh_token: data.refresh_token,

          name: data.name,
          company: data.company,
          email: data.email,
          role: data.role,
          profile_image: data.profile_image,

          firststart: data.firststart,
          firststart_done: data.firststart_done,
          version: data.version,
          appdata: data.appdata,
          userid: data.userid,
          fid: data.fid,

          isactivated: data.isactivated,
          isapproved: data.isapproved,
          status: data.status,

          country: data.country,

          phone_id: data.phone_id,
          gemeinde_key: data.gemeinde_key,
          accesspoint: data.accesspoint,
          request_ip: data.request_ip,

          dienstnr: data.dienstnr,
          dienstname: data.dienstname,
          dienstnr_stvo: data.dienstnr_stvo,
          sachbereich: data.sachbereich,
        };

        this.authStore.save(auth);
      })
    );
  }

  firststart(firststartData: any) {
    // do the login actions
    return this.authHttpService.firststart(firststartData).pipe(
      tap((data) => {
        // we have to do some mapping
        // as the return is more than
        // we want in the auth store
        let auth = {
          appid: data.appid,
          access_token: data.access_token,
          token_type: data.token_type,
          expires_in: data.expires_in,
          refresh_token: data.refresh_token,

          name: data.name,
          company: data.company,
          email: data.email,
          role: data.role,
          profile_image: data.profile_image,

          firststart: data.firststart,
          firststart_done: data.firststart_done,
          version: data.version,
          appdata: data.appdata,
          userid: data.userid,
          fid: data.fid,

          isactivated: data.isactivated,
          isapproved: data.isapproved,
          status: data.status,

          country: data.country,

          phone_id: data.phone_id,
          gemeinde_key: data.gemeinde_key,
          accesspoint: data.accesspoint,
          request_ip: data.request_ip,

          dienstnr: data.dienstnr,
          dienstname: data.dienstname,
          dienstnr_stvo: data.dienstnr_stvo,
          sachbereich: data.sachbereich,
        };
        this.authStore.save(auth);
      })
    );
  }

  updateVersion(version: any) {
    this.authStore.update({ version: version });
  }

  updateFirststart(firststart: any) {
    // Update both firststart and firststart_done if provided
    const currentState = this.authStore.getValue();
    const updateData: any = { ...currentState };
    
    if (firststart.firststart !== undefined) {
      updateData.firststart = firststart.firststart;
    }
    if (firststart.firststart_done !== undefined) {
      updateData.firststart_done = firststart.firststart_done;
    }
    
    // Update store immediately (synchronous)
    this.authStore.update(updateData);
    
    // Also save to store and persist to session storage
    this.authStore.save(updateData);
    
    console.log('updateFirststart: Updated firststart_done to', updateData.firststart_done);
  }

  changepwd(data: any) {
    return this.authHttpService.changePassword(data).pipe(
      tap((data) => {
        // we have to do some mapping
      })
    );
  }

  forgotpwd(data: any) {
    return this.authHttpService.forgotPassword(data).pipe(
      tap((data) => {
        // we have to do some mapping
      })
    );
  }

  loginWithToken(token: string, accountId: number) {
    return this.authHttpService.loginWithToken(token, accountId).pipe(
      tap((data: any) => {
        // Map the response to auth store format
        // Handle both old BackdoorLoginAction format and new format
        let auth = {
          appid: data.appid || data.appdataid || null,
          access_token: data.access_token || token,
          token_type: data.token_type || 'Bearer',
          expires_in: data.expires_in || 3600,
          refresh_token: data.refresh_token || '',

          name: data.name || (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}`.trim() : ''),
          company: data.company || '',
          email: data.email || '',
          role: data.role_id || parseInt(data.role) || data.role || 0,
          profile_image: data.profile_image || '',

          firststart: data.firststart || 0,
          firststart_done: data.firststart_done || data.first_start_done || 0,
          version: data.version || 1,
          appdata: data.appdata || (data.appdata ? JSON.parse(data.appdata) : {}),
          userid: data.userid || accountId,
          fid: data.fid || data.userid || accountId,

          isactivated: data.isactivated || data.is_activated || 1,
          isapproved: data.isapproved || data.is_approved || 1,
          status: data.status || 1,

          country: data.country || 'at',

          phone_id: data.phone_id || '',
          gemeinde_key: data.gemeinde_key || '',
          accesspoint: data.accesspoint || '',
          request_ip: data.request_ip || '',

          dienstnr: data.dienstnr || '',
          dienstname: data.dienstname || '',
          dienstnr_stvo: data.dienstnr_stvo || '',
          sachbereich: data.sachbereich || '',
        };

        this.authStore.save(auth);
        
        // Set permissions in permission service if available
        if (data.permissions) {
          this.permissionService.setUserPermissions(data);
        }
      })
    );
  }

  activateAccount(token: string, accountId: number) {
    return this.authHttpService.activateAccount(token, accountId).pipe(
      tap((data: any) => {
        if (data.success) {
          // Save auth state with tokens
          let auth = {
            appid: data.user?.id || 0,
            access_token: data.access_token,
            token_type: data.token_type,
            expires_in: data.expires_in,
            refresh_token: data.refresh_token,

            name: data.user?.name || '',
            company: '',
            email: data.user?.email || '',
            role: data.user?.role || 0,
            profile_image: '',

            firststart: '0', // Must be a string to match AuthState type
            firststart_done: data.requires_password_change ? 0 : 1,
            version: 0,
            appdata: '{}', // Must be a JSON string, not an object
            userid: data.user?.id || 0,
            fid: data.user?.fid || 0,

            isactivated: 1,
            isapproved: 1,
            status: 1,

            country: 'at',

            phone_id: '',
            gemeinde_key: '',
            accesspoint: '',
            request_ip: '',

            dienstnr: '0', // Must be a string to match AuthState type
            dienstname: data.user?.name || '',
            dienstnr_stvo: '0', // Must be a string to match AuthState type
            sachbereich: '',
          };

          this.authStore.save(auth);
          
          // Set permissions in permission service if available
          if (data.permissions) {
            this.permissionService.setUserPermissions(data);
          }
        }
      })
    );
  }

  sendEmailVerification(activationToken?: string, accountId?: number) {
    const token = this.authQuery.access_token;
    return this.authHttpService.sendEmailVerification(token || undefined, activationToken, accountId);
  }

  verifyEmailCode(code: string, activationToken?: string, accountId?: number) {
    const token = this.authQuery.access_token;
    return this.authHttpService.verifyEmailCode(code, token || undefined, activationToken, accountId);
  }

  requestNewActivationToken(email: string) {
    return this.authHttpService.requestNewActivationToken(email);
  }

  logout() {
    return this.authHttpService.logout().pipe(
      tap((auth) => {
        //console.log('remove all stores');
        this.authStore.remove();
        this.permissionService.clearUserPermissions();
        resetStores({
          //exclude: ['storeNameToKeep']
        });

        // Clear localStorage or sessionStorage where Akita's persistState might have stored data
        // If you are using specific keys for persistState, you might want to clear those specifically
        // For example: localStorage.removeItem('persistedStoreKey');
        localStorage.clear(); // Use with caution; this clears everything in localStorage
      })
    );
  }
}
